import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, Calendar, Clock, X } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./Input";

// Date Picker Types
export interface DatePickerProps {
  value?: Date | Date[] | { start: Date; end: Date };
  onChange?: (value: Date | Date[] | { start: Date; end: Date } | null) => void;
  variant?: "single" | "range" | "multiple";
  withTime?: boolean;
  inline?: boolean;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  disabledDays?: number[]; // 0 = Sunday, 1 = Monday, etc.
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

// Time Picker Types
export interface TimePickerProps {
  value?: Date;
  onChange?: (value: Date | null) => void;
  variant?: "dropdown" | "scrollable" | "inline";
  format?: "12h" | "24h";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  error?: string;
  className?: string;
}

// DateTime Picker Types
export interface DateTimePickerProps {
  value?: Date;
  onChange?: (value: Date | null) => void;
  variant?: "inline" | "dropdown";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
}

// Date Picker Component
export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  variant = "single",
  inline = false,
  size = "md",
  disabled = false,
  error,
  placeholder = "Datum wählen",
  disabledDays = [],
  minDate,
  maxDate,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Initialize selected dates from value prop
  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        setSelectedDates(value);
      } else if (typeof value === "object" && "start" in value && "end" in value) {
        setRangeStart(value.start);
        setRangeEnd(value.end);
      } else {
        setSelectedDates([value]);
      }
    }
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sizeStyles = {
    sm: { input: "text-xs px-2 py-1", cell: "text-xs p-1", header: "text-xs" },
    md: { input: "text-sm px-3 py-2", cell: "text-sm p-2", header: "text-sm" },
    lg: {
      input: "text-base px-4 py-3",
      cell: "text-base p-3",
      header: "text-base",
    },
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("de-DE");
  };

  const formatValue = () => {
    if (variant === "range" && rangeStart && rangeEnd) {
      return `${formatDate(rangeStart)} - ${formatDate(rangeEnd)}`;
    } else if (variant === "multiple" && selectedDates.length > 0) {
      return selectedDates.map(formatDate).join(", ");
    } else if (variant === "single" && selectedDates.length > 0 && selectedDates[0]) {
      return formatDate(selectedDates[0]);
    }
    return "";
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateSelected = (date: Date) => {
    if (variant === "range") {
      return (
        (rangeStart && date.getTime() === rangeStart.getTime()) ||
        (rangeEnd && date.getTime() === rangeEnd.getTime())
      );
    } else {
      return selectedDates.some((d) => d.getTime() === date.getTime());
    }
  };

  const isDateInRange = (date: Date) => {
    if (variant !== "range" || !rangeStart || !rangeEnd) return false;
    return date > rangeStart && date < rangeEnd;
  };

  const isDateDisabled = (date: Date) => {
    const dayOfWeek = date.getDay();
    if (disabledDays.includes(dayOfWeek)) return true;
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    if (variant === "range") {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(date);
        setRangeEnd(null);
      } else {
        if (date < rangeStart) {
          setRangeEnd(rangeStart);
          setRangeStart(date);
        } else {
          setRangeEnd(date);
        }
      }
    } else if (variant === "multiple") {
      const isSelected = selectedDates.some((d) => d.getTime() === date.getTime());
      if (isSelected) {
        setSelectedDates(selectedDates.filter((d) => d.getTime() !== date.getTime()));
      } else {
        setSelectedDates([...selectedDates, date]);
      }
    } else {
      setSelectedDates([date]);
    }
  };

  const handleMonthChange = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      if (direction === "prev") {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleYearChange = (year: number) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setFullYear(year);
      return newMonth;
    });
  };

  const handleMonthSelect = (month: number) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(month);
      return newMonth;
    });
  };

  // Generate year options (current year ± 100 years for birthdates)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  };

  const handleApply = () => {
    let result: Date | Date[] | { start: Date; end: Date } | null = null;

    if (variant === "range" && rangeStart && rangeEnd) {
      result = { start: rangeStart, end: rangeEnd };
    } else if (variant === "multiple" && selectedDates.length > 0) {
      result = selectedDates;
    } else if (variant === "single" && selectedDates.length > 0 && selectedDates[0]) {
      result = selectedDates[0];
    }

    onChange?.(result);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedDates([]);
    setRangeStart(null);
    setRangeEnd(null);
    onChange?.(null);
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];
  const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

  const calendar = (
    <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => handleMonthChange("prev")}>
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {/* Month and Year Dropdowns */}
        <div className="flex items-center gap-2">
          <select
            value={currentMonth.getMonth()}
            onChange={(e) => handleMonthSelect(parseInt(e.target.value))}
            className={`bg-slate-800/30 backdrop-blur-sm border border-slate-600/50 rounded-lg px-3 py-1.5 text-gray-200 ${sizeStyles[size].header} focus:border-tracq-bright-blue/70 focus:ring-tracq-bright-blue/20 focus:outline-none transition-all duration-200 hover:bg-slate-700/40 hover:border-slate-500/60 shadow-lg`}
            style={{
              background:
                "linear-gradient(135deg, rgba(30, 41, 59, 0.3) 0%, rgba(51, 65, 85, 0.2) 100%)",
              backdropFilter: "blur(8px)",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              maxHeight: "200px",
              overflowY: "auto",
            }}
            size={1}
          >
            {monthNames.map((month, index) => (
              <option key={index} value={index} className="bg-slate-800 text-gray-200 py-1">
                {month}
              </option>
            ))}
          </select>

          <select
            value={currentMonth.getFullYear()}
            onChange={(e) => handleYearChange(parseInt(e.target.value))}
            className={`bg-slate-800/30 backdrop-blur-sm border border-slate-600/50 rounded-lg px-3 py-1.5 text-gray-200 ${sizeStyles[size].header} focus:border-tracq-bright-blue/70 focus:ring-tracq-bright-blue/20 focus:outline-none transition-all duration-200 hover:bg-slate-700/40 hover:border-slate-500/60 shadow-lg`}
            style={{
              background:
                "linear-gradient(135deg, rgba(30, 41, 59, 0.3) 0%, rgba(51, 65, 85, 0.2) 100%)",
              backdropFilter: "blur(8px)",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              maxHeight: "200px",
              overflowY: "auto",
            }}
            size={1}
          >
            {generateYearOptions().map((year) => (
              <option key={year} value={year} className="bg-slate-800 text-gray-200 py-1">
                {year}
              </option>
            ))}
          </select>
        </div>

        <Button variant="ghost" size="sm" onClick={() => handleMonthChange("next")}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div key={day} className={`text-center text-gray-400 ${sizeStyles[size].cell}`}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className={sizeStyles[size].cell} />;
          }

          const isSelected = isDateSelected(date);
          const isInRange = isDateInRange(date);
          const isDisabled = isDateDisabled(date);

          return (
            <button
              key={date.getTime()}
              className={clsx("rounded transition-colors", sizeStyles[size].cell, {
                "text-gray-200 hover:bg-gray-800": !isSelected && !isInRange && !isDisabled,
                "bg-tracq-bright-blue text-white": isSelected,
                "bg-tracq-bright-blue/20 text-tracq-bright-blue": isInRange,
                "text-gray-500 cursor-not-allowed": isDisabled,
              })}
              onClick={() => handleDateClick(date)}
              disabled={isDisabled}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
        <Button variant="ghost" size="sm" onClick={handleClear}>
          <X className="w-4 h-4 mr-2" />
          Löschen
        </Button>
        <Button size="sm" onClick={handleApply}>
          Anwenden
        </Button>
      </div>
    </div>
  );

  if (inline) {
    return <div className={className}>{calendar}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={formatValue()}
        placeholder={placeholder}
        readOnly
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        error={!!error}
        className={sizeStyles[size].input}
        icon={<Calendar className="w-4 h-4" />}
      />

      {isOpen && (
        <div ref={calendarRef} className="absolute top-full left-0 mt-1 z-50">
          {calendar}
        </div>
      )}
    </div>
  );
};

// Time Picker Component
export const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  variant = "dropdown",
  format = "24h",
  size = "md",
  disabled = false,
  error,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState<Date>(value || new Date());
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      setSelectedTime(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sizeStyles = {
    sm: { input: "text-xs px-2 py-1", item: "text-xs px-2 py-1" },
    md: { input: "text-sm px-3 py-2", item: "text-sm px-3 py-2" },
    lg: { input: "text-base px-4 py-3", item: "text-base px-4 py-3" },
  };

  const formatTime = (date: Date) => {
    if (format === "12h") {
      return date.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      return date.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
  };

  const generateTimeOptions = () => {
    const options = [];
    const maxHour = format === "12h" ? 12 : 24;
    const startHour = format === "12h" ? 1 : 0;

    for (let hour = startHour; hour < maxHour; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = new Date();
        time.setHours(format === "12h" ? hour : hour, minute, 0, 0);
        options.push(time);
      }
    }
    return options;
  };

  const handleTimeSelect = (time: Date) => {
    setSelectedTime(time);
    onChange?.(time);
    setIsOpen(false);
  };

  const timeOptions = generateTimeOptions();

  if (variant === "inline") {
    return (
      <Input
        type="time"
        value={selectedTime.toTimeString().slice(0, 5)}
        onChange={(e) => {
          const [hoursStr, minutesStr] = e.target.value.split(":");
          const hours = hoursStr ? Number(hoursStr) : 0;
          const minutes = minutesStr ? Number(minutesStr) : 0;
          const newTime = new Date();
          newTime.setHours(hours, minutes, 0, 0);
          setSelectedTime(newTime);
          onChange?.(newTime);
        }}
        disabled={disabled}
        error={!!error}
        className={clsx(sizeStyles[size].input, className)}
        icon={<Clock className="w-4 h-4" />}
      />
    );
  }

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={formatTime(selectedTime)}
        placeholder="Zeit wählen"
        readOnly
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        error={!!error}
        className={sizeStyles[size].input}
        icon={<Clock className="w-4 h-4" />}
      />

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50"
        >
          {timeOptions.map((time, index) => (
            <button
              key={index}
              className={clsx("w-full text-left transition-colors", sizeStyles[size].item, {
                "text-gray-200 hover:bg-gray-800": selectedTime.getTime() !== time.getTime(),
                "bg-tracq-bright-blue text-white": selectedTime.getTime() === time.getTime(),
              })}
              onClick={() => handleTimeSelect(time)}
            >
              {formatTime(time)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// DateTime Picker Component
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  variant = "dropdown",
  size = "md",
  disabled = false,
  error,
  placeholder = "Datum und Zeit wählen",
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date>(value || new Date());
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      setSelectedDateTime(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sizeStyles = {
    sm: { input: "text-xs px-2 py-1" },
    md: { input: "text-sm px-3 py-2" },
    lg: { input: "text-base px-4 py-3" },
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDateChange = (value: Date | Date[] | { start: Date; end: Date } | null) => {
    if (value && !Array.isArray(value) && !("start" in value)) {
      const newDateTime = new Date(value);
      newDateTime.setHours(selectedDateTime.getHours(), selectedDateTime.getMinutes());
      setSelectedDateTime(newDateTime);
    }
  };

  const handleTimeChange = (time: Date | null) => {
    if (time) {
      const newDateTime = new Date(selectedDateTime);
      newDateTime.setHours(time.getHours(), time.getMinutes());
      setSelectedDateTime(newDateTime);
      onChange?.(newDateTime);
    }
  };

  const handleApply = () => {
    onChange?.(selectedDateTime);
    setIsOpen(false);
  };

  if (variant === "inline") {
    return (
      <div className={clsx("space-y-4", className)}>
        <DatePicker
          value={selectedDateTime}
          onChange={handleDateChange}
          inline
          size={size}
          disabled={disabled}
        />
        <TimePicker
          value={selectedDateTime}
          onChange={handleTimeChange}
          variant="dropdown"
          size={size}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={formatDateTime(selectedDateTime)}
        placeholder={placeholder}
        readOnly
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        error={!!error}
        className={sizeStyles[size].input}
        icon={<Calendar className="w-4 h-4" />}
      />

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-4 z-50"
        >
          <div className="space-y-4">
            <DatePicker
              value={selectedDateTime}
              onChange={handleDateChange}
              inline
              size={size}
              disabled={disabled}
            />
            <TimePicker
              value={selectedDateTime}
              onChange={handleTimeChange}
              variant="dropdown"
              size={size}
              disabled={disabled}
            />
            <div className="flex justify-end pt-4 border-t border-gray-700">
              <Button size="sm" onClick={handleApply}>
                Anwenden
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
